/** Super-admin: view and adjust one tenant's AI wallet.
 *
 * The screen talks RUPEES and the API talks paise, and the conversion
 * happens here, once, at the edge. That is the whole reason this card was
 * rewritten: it used to take a number of CREDITS, which meant a different
 * amount of AI depending on which model a learner later picked, and the
 * only way to find out what you had granted was to multiply by a
 * conversion factor shown underneath the field.
 *
 * The adjust form REQUIRES a reason, mirroring the backend — an
 * unexplained balance change is exactly what the ledger exists to prevent,
 * so the UI should not offer one either.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/store";
import type { CreditBalance } from "../../api/store";
import { ApiError } from "../../api/http";
import { Button, Card, Input, Label } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";
import { rupeeCost } from "../../utils/rupees";

/** A rupee figure typed into a box → paise, or null if it is not one.
 *
 * Rounded rather than truncated, and done with a multiply on a parsed
 * float at this single boundary: "12.5" has to mean 1,250 paise, and
 * floating point would otherwise hand the server 1249.9999999999998. */
function toMinor(text: string): number | null {
  const rupees = Number(text.trim());
  if (!Number.isFinite(rupees)) return null;
  return Math.round(rupees * 100);
}

export function TenantCredits({ tenantId }: { tenantId: string }) {
  const t = useT();
  const { push } = useToast();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [monthly, setMonthly] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingMonthly, setSavingMonthly] = useState(false);

  const showMonthly = (b: CreditBalance) => String(b.monthly_minor / 100);

  useEffect(() => {
    api.tenantCredits(tenantId)
      .then((b) => {
        setBalance(b);
        setMonthly(showMonthly(b));
      })
      .catch(() => setBalance(null));
  }, [tenantId]);

  const saveMonthly = async () => {
    const minor = toMinor(monthly);
    if (minor === null || minor < 0) return;
    setSavingMonthly(true);
    try {
      const updated = await api.setMonthlyAllowance(tenantId, minor);
      setBalance(updated);
      setMonthly(showMonthly(updated));
      push("success", t("admin.credits.monthlySaved"));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("admin.credits.failed"));
    } finally {
      setSavingMonthly(false);
    }
  };

  const submit = async () => {
    const minor = toMinor(amount);
    if (minor === null || minor === 0 || !reason.trim()) return;
    setBusy(true);
    try {
      const updated = await api.adjustTenantCredits(tenantId, minor, reason.trim());
      setBalance(updated);
      setAmount("");
      setReason("");
      push("success", t("admin.credits.done"));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("admin.credits.failed"));
    } finally {
      setBusy(false);
    }
  };

  const monthlyMinor = toMinor(monthly);

  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-bold text-ink">{t("admin.credits.title")}</h2>
        <span className="text-2xl font-extrabold text-ink">
          {balance ? rupeeCost(balance.balance) : "—"}
        </span>
      </div>
      {balance && (
        <p className="mt-1 text-xs text-muted">
          {t("admin.credits.thisMonth", {
            granted: rupeeCost(balance.granted_this_month),
            spent: rupeeCost(balance.spent_this_month),
          })}
        </p>
      )}

      {/* The recurring allowance. Set once; every month from now on the
          tenant is topped up to this figure automatically. */}
      <div className="mt-4 border-t border-line pt-4">
        <Label htmlFor="credit-monthly">{t("admin.credits.monthly")}</Label>
        <p className="mt-1 text-xs text-muted">{t("admin.credits.monthlyHint")}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* The unit is in the label beside the box, not in a hint under
              it: a figure typed into an unlabelled field is a guess. */}
          <span className="text-sm font-semibold text-muted">₹</span>
          <Input
            id="credit-monthly"
            type="number"
            min={0}
            step="1"
            className="w-32"
            value={monthly}
            onChange={(e) => setMonthly(e.target.value)}
          />
          <span className="text-sm text-muted">{t("admin.credits.perMonth")}</span>
          <Button
            variant="secondary"
            onClick={saveMonthly}
            disabled={
              savingMonthly || monthly === "" ||
              monthlyMinor === null || monthlyMinor < 0
            }
          >
            {t("admin.credits.monthlySave")}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 border-t border-line pt-4 sm:grid-cols-[8rem_1fr]">
        <div className="flex flex-col gap-1">
          <Label htmlFor="credit-amount">{t("admin.credits.amount")}</Label>
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-muted">₹</span>
            <Input
              id="credit-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="±"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="credit-reason">{t("admin.credits.reason")}</Label>
          <Input
            id="credit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("admin.credits.reasonHint")}
          />
        </div>
      </div>
      <Button
        className="mt-3"
        onClick={submit}
        disabled={busy || !amount || toMinor(amount) === 0 || !reason.trim()}
      >
        {t("admin.credits.apply")}
      </Button>
    </Card>
  );
}
